import { FormFieldInputType } from 'models/enum';

export enum FormField {
  PATIENT_NAME = 'patientName',
  AGE = 'age',
  PROVIDER = 'provider',
  ALLERGIES = 'allergies',
  ARRIVAL_TIME = 'arrivalTime',
  MODE = 'mode',
  OTHER = 'other',
  REASON_FOR_ARRIVAL = 'reasonForArrival',
  TIME_OF_ONSET = 'timeOfOnset',
  CONTRACTION_FREQUENCY = 'contractionFrequency',
  CONTRACTION_DURATION = 'contractionDuration',
  CONTRACTION_QUALITY = 'contractionQuality',
  MEMBRANES_STATUS = 'membranesStatus',
  MEMBRANES_TIME = 'membranesTime',
  COLOR_OF_FLUID = 'colorOfFluid',
  VISCOSITY = 'viscosity',
  ODOR = 'odor',
  AMOUNT = 'amount',
  FETAL_ACTIVITY = 'fetalActivity',
  TIME_OF_LAST_ACTIVITY = 'timeOfLastActivity',
  CURRENT_PRENATAL_CARE = 'currentPrenatalCare',
  STEP2_PROVIDER_NAME = 'step2ProviderName',
  LMP_DAYS = 'lmpDays',
  LMP_WEEKS = 'lmpWeeks',
  LMP_HOW_LONG_AGO_UNIT = 'lmpHowLongAgoUnit',
  CERTAINTY = 'certainty',
  EDB_DAYS_FROM_NOW = 'edbDaysFromNow',
  EDB_WEEKS_FROM_NOW = 'edbWeeksFromNow',
  EDB_FROM_NOW_UNIT = 'edbFromNowUnit',
  CONFIRMED_BY_ULTRASOUND = 'confirmedByUltrasound',
  STEP2_GESTATIONAL_AGE = 'step2GestationalAge',
  STEP2_GRAVIDA = 'step2Gravida',
  STEP2_PARA = 'step2Para',
  STEP2_TERM = 'step2Term',
  STEP2_PRETERM = 'step2Preterm',
  STEP2_ABORTION = 'step2Abortion',
  STEP2_LIVING = 'step2Living',
  GROUP_B_STREP_STATUS = 'groupBStrepStatus',
  ISSUES_CONCERNS = 'issuesConcerns',
  PREVIOUS_HOSPITALIZATIONS_DURING_PREGNANCY = 'previousHospitalizationsDuringPregnancy',
  FOCUSED_HISTORY_ALCOHOL_USE = 'focusedHistoryAlcoholUse',
  FOCUSED_HISTORY_ALCOHOL_USE_DETAILS = 'focusedHistoryAlcoholUseDetails',
  FOCUSED_HISTORY_STREET_DRUG_USE = 'focusedHistoryStreetDrugUse',
  FOCUSED_HISTORY_STREET_DRUG_USE_DETAILS = 'focusedHistoryStreetDrugUseDetails',
  FOCUSED_HISTORY_INTRAVENOUS_DRUG_USE = 'focusedHistoryIntravenousDrugUse',
  FOCUSED_HISTORY_INTRAVENOUS_DRUG_USE_DETAILS = 'focusedHistoryIntravenousDrugUseDetails',
  FOCUSED_HISTORY_HIV_POSITIVE = 'focusedHistoryHivPositive',
  FOCUSED_HISTORY_HIV_POSITIVE_DETAILS = 'focusedHistoryHivPositiveDetails',
  FOCUSED_HISTORY_HEPATITIS_B_POSITIVE = 'focusedHistoryHepatitisBPositive',
  FOCUSED_HISTORY_HEPATITIS_B_POSITIVE_DETAILS = 'focusedHistoryHepatitisBPositiveDetails',
  ORAL_INTAKE_TIME_LAST_FOOD_OR_LIQUID = 'oralIntakeTimeLastFoodOrLiquid',
  ORAL_INTAKE_DESCRIBE = 'oralIntakeDescribe',
  INITIAL_FETAL_HEART_RATE_PATTERN = 'initialFetalHeartRatePattern',
  INITIAL_COMMENTS = 'initialComments',
  INITIAL_VAGINAL_DISCHARGE = 'initialVaginalDischarge',
  INITIAL_COLOR = 'initialColor',
  INITIAL_ODOR = 'initialOdor',
  INITIAL_PH = 'initialPh',
  INITIAL_OTHER = 'initialOther',
  INITIAL_FERNING = 'initialFerning',
  INITIAL_PROTEIN = 'initialProtein',
  INITIAL_GLUCOSE = 'initialGlucose',
  INITIAL_GROUP_B_STREP_STATUS = 'initialGroupBStrepStatus',
  INITIAL_WBCS = 'initialWbcs',
  INITIAL_RBCS = 'initialRbcs',
  PRENATAL_CARE_PROVIDER = 'prenatalCareProvider',
  GRAVIDA_PARA = 'gravidaPara',
  ESTIMATED_DELIVERY_DATE = 'estimatedDeliveryDate',
  GESTATIONAL_AGE = 'gestationalAge',
  PRENATAL_COMPLICATIONS = 'prenatalComplications',
  PRENATAL_NOTES = 'prenatalNotes',
  MATERNAL_BLOOD_PRESSURE = 'maternalBloodPressure',
  MATERNAL_PULSE = 'maternalPulse',
  MATERNAL_TEMPERATURE = 'maternalTemperature',
  MATERNAL_RESPIRATIONS = 'maternalRespirations',
  CERVICAL_DILATION = 'cervicalDilation',
  EFFACEMENT = 'effacement',
  STATION = 'station',
  FETAL_HEART_RATE = 'fetalHeartRate',
  INITIAL_ASSESSMENT_NOTES = 'initialAssessmentNotes',
  PROVIDER_NOTIFIED_TIME = 'providerNotifiedTime',
  PROVIDER_ORDERS = 'providerOrders',
  MEDICATIONS = 'medications',
  LABS_AND_DIAGNOSTICS = 'labsAndDiagnostics',
  INTERVENTIONS = 'interventions',
  PATIENT_RESPONSE_NOTES = 'patientResponseNotes',
  ADDITIONAL_NOTES = 'additionalNotes',
  NOTES = 'notes',
  NURSE_SIGNATURE = 'nurseSignature',
  NURSE_SIGNATURE_TIME = 'nurseSignatureTime',
  PROVIDER_SIGNATURE = 'providerSignature',
  PROVIDER_SIGNATURE_TIME = 'providerSignatureTime'
}

export enum SectionTitle {
  OB_TRIAGE = 'OB Triage'
}

export enum ViewTitle {
  OB_TRIAGE_RECORD = 'OB Triage Record'
}

export interface OBTriageFieldConfig {
  name: FormField;
  label: string;
  type: FormFieldInputType;
  isRequired?: boolean;
  displayHorizontal?: boolean;
  maxLength?: number;
}

export interface OBTriageSectionConfig {
  title: string;
  rows: FormField[][];
}

export interface OBTriageStepConfig {
  title: string;
  sections: OBTriageSectionConfig[];
}

const DEFAULT_TEXT_INPUT_MAX_LENGTH = 150;

const withTextInputMaxLength = (fieldConfig: OBTriageFieldConfig): OBTriageFieldConfig =>
  fieldConfig.type === FormFieldInputType.TEXT_BOX ? { ...fieldConfig, maxLength: fieldConfig.maxLength ?? DEFAULT_TEXT_INPUT_MAX_LENGTH } : fieldConfig;

export const FIELD_CONFIGS: OBTriageFieldConfig[] = [
  { name: FormField.PATIENT_NAME, label: 'Patient Name', type: FormFieldInputType.TEXT_BOX },
  { name: FormField.AGE, label: 'Age', type: FormFieldInputType.TEXT_BOX },
  { name: FormField.PROVIDER, label: 'Provider', type: FormFieldInputType.TEXT_BOX },
  { name: FormField.ALLERGIES, label: 'Allergies', type: FormFieldInputType.TEXT_BOX },
  { name: FormField.ARRIVAL_TIME, label: 'Arrival Time', type: FormFieldInputType.TIME },
  { name: FormField.MODE, label: 'Mode', type: FormFieldInputType.TEXT_BOX },
  { name: FormField.OTHER, label: 'Other', type: FormFieldInputType.TEXT_BOX },
  { name: FormField.REASON_FOR_ARRIVAL, label: 'Reason for Arrival', type: FormFieldInputType.DROPDOWN },
  { name: FormField.TIME_OF_ONSET, label: 'Time of Onset', type: FormFieldInputType.TIME },
  { name: FormField.CONTRACTION_FREQUENCY, label: 'Frequency', type: FormFieldInputType.TEXT_BOX },
  { name: FormField.CONTRACTION_DURATION, label: 'Duration', type: FormFieldInputType.TEXT_BOX },
  { name: FormField.CONTRACTION_QUALITY, label: 'Quality', type: FormFieldInputType.TEXT_BOX },
  { name: FormField.MEMBRANES_STATUS, label: '', type: FormFieldInputType.RADIO_CHOICE, displayHorizontal: true },
  { name: FormField.MEMBRANES_TIME, label: 'Time', type: FormFieldInputType.TIME },
  { name: FormField.COLOR_OF_FLUID, label: 'Color of Fluid', type: FormFieldInputType.DROPDOWN },
  { name: FormField.VISCOSITY, label: 'Viscosity', type: FormFieldInputType.DROPDOWN },
  { name: FormField.ODOR, label: 'Odor', type: FormFieldInputType.DROPDOWN },
  { name: FormField.AMOUNT, label: 'Amount', type: FormFieldInputType.DROPDOWN },
  { name: FormField.FETAL_ACTIVITY, label: 'Active:', type: FormFieldInputType.RADIO_CHOICE, displayHorizontal: true },
  { name: FormField.TIME_OF_LAST_ACTIVITY, label: 'Time of Last Activity', type: FormFieldInputType.TIME },
  { name: FormField.CURRENT_PRENATAL_CARE, label: 'Currently receiving pre-natal care?', type: FormFieldInputType.RADIO_CHOICE, displayHorizontal: true },
  { name: FormField.STEP2_PROVIDER_NAME, label: 'Provider Name', type: FormFieldInputType.TEXT_BOX },
  { name: FormField.LMP_DAYS, label: 'Days', type: FormFieldInputType.TEXT_BOX },
  { name: FormField.LMP_WEEKS, label: 'Weeks', type: FormFieldInputType.TEXT_BOX },
  { name: FormField.LMP_HOW_LONG_AGO_UNIT, label: 'How Long Ago?', type: FormFieldInputType.RADIO_CHOICE },
  { name: FormField.CERTAINTY, label: 'Certainty', type: FormFieldInputType.DROPDOWN },
  { name: FormField.EDB_DAYS_FROM_NOW, label: 'Days from now', type: FormFieldInputType.TEXT_BOX },
  { name: FormField.EDB_WEEKS_FROM_NOW, label: 'Weeks from now', type: FormFieldInputType.TEXT_BOX },
  { name: FormField.EDB_FROM_NOW_UNIT, label: '', type: FormFieldInputType.RADIO_CHOICE },
  { name: FormField.CONFIRMED_BY_ULTRASOUND, label: 'Confirmed by Ultrasound?', type: FormFieldInputType.RADIO_CHOICE, displayHorizontal: true },
  { name: FormField.STEP2_GESTATIONAL_AGE, label: 'Gestational Age', type: FormFieldInputType.TEXT_BOX },
  { name: FormField.STEP2_GRAVIDA, label: 'Gravida', type: FormFieldInputType.TEXT_BOX },
  { name: FormField.STEP2_PARA, label: 'Para', type: FormFieldInputType.TEXT_BOX },
  { name: FormField.STEP2_TERM, label: 'Term', type: FormFieldInputType.TEXT_BOX },
  { name: FormField.STEP2_PRETERM, label: 'Pre-Term', type: FormFieldInputType.TEXT_BOX },
  { name: FormField.STEP2_ABORTION, label: 'Abortion', type: FormFieldInputType.TEXT_BOX },
  { name: FormField.STEP2_LIVING, label: 'Living', type: FormFieldInputType.TEXT_BOX },
  { name: FormField.GROUP_B_STREP_STATUS, label: 'Group B Streptococcus Status', type: FormFieldInputType.DROPDOWN },
  { name: FormField.ISSUES_CONCERNS, label: 'Issues/Concerns', type: FormFieldInputType.TEXT_AREA, maxLength: 150 },
  {
    name: FormField.PREVIOUS_HOSPITALIZATIONS_DURING_PREGNANCY,
    label: 'Previous Hospitalizations during Pregnancy',
    type: FormFieldInputType.TEXT_AREA,
    maxLength: 150
  },
  { name: FormField.FOCUSED_HISTORY_ALCOHOL_USE, label: '', type: FormFieldInputType.CHECK_BOX },
  { name: FormField.FOCUSED_HISTORY_ALCOHOL_USE_DETAILS, label: '', type: FormFieldInputType.TEXT_BOX },
  { name: FormField.FOCUSED_HISTORY_STREET_DRUG_USE, label: '', type: FormFieldInputType.CHECK_BOX },
  { name: FormField.FOCUSED_HISTORY_STREET_DRUG_USE_DETAILS, label: '', type: FormFieldInputType.TEXT_BOX },
  { name: FormField.FOCUSED_HISTORY_INTRAVENOUS_DRUG_USE, label: '', type: FormFieldInputType.CHECK_BOX },
  { name: FormField.FOCUSED_HISTORY_INTRAVENOUS_DRUG_USE_DETAILS, label: '', type: FormFieldInputType.TEXT_BOX },
  { name: FormField.FOCUSED_HISTORY_HIV_POSITIVE, label: '', type: FormFieldInputType.CHECK_BOX },
  { name: FormField.FOCUSED_HISTORY_HIV_POSITIVE_DETAILS, label: '', type: FormFieldInputType.TEXT_BOX },
  { name: FormField.FOCUSED_HISTORY_HEPATITIS_B_POSITIVE, label: '', type: FormFieldInputType.CHECK_BOX },
  { name: FormField.FOCUSED_HISTORY_HEPATITIS_B_POSITIVE_DETAILS, label: '', type: FormFieldInputType.TEXT_BOX },
  {
    name: FormField.ORAL_INTAKE_TIME_LAST_FOOD_OR_LIQUID,
    label: 'Time of last food or liquid',
    type: FormFieldInputType.TIME
  },
  { name: FormField.ORAL_INTAKE_DESCRIBE, label: 'Describe', type: FormFieldInputType.TEXT_BOX },
  { name: FormField.INITIAL_FETAL_HEART_RATE_PATTERN, label: 'Fetal Heart Rate Pattern', type: FormFieldInputType.DROPDOWN },
  { name: FormField.INITIAL_COMMENTS, label: 'Comments', type: FormFieldInputType.TEXT_AREA, maxLength: 150 },
  { name: FormField.INITIAL_VAGINAL_DISCHARGE, label: 'Vaginal Discharge', type: FormFieldInputType.DROPDOWN },
  { name: FormField.INITIAL_COLOR, label: 'Color', type: FormFieldInputType.DROPDOWN },
  { name: FormField.INITIAL_ODOR, label: 'Odor', type: FormFieldInputType.RADIO_CHOICE, displayHorizontal: true },
  { name: FormField.INITIAL_PH, label: 'pH', type: FormFieldInputType.TEXT_BOX },
  { name: FormField.INITIAL_OTHER, label: 'Other', type: FormFieldInputType.TEXT_BOX },
  { name: FormField.INITIAL_FERNING, label: 'Ferning', type: FormFieldInputType.RADIO_CHOICE, displayHorizontal: true },
  { name: FormField.INITIAL_GROUP_B_STREP_STATUS, label: 'Group B Streptococcus Status', type: FormFieldInputType.RADIO_CHOICE, displayHorizontal: true },
  { name: FormField.INITIAL_PROTEIN, label: 'Protein', type: FormFieldInputType.TEXT_BOX },
  { name: FormField.INITIAL_GLUCOSE, label: 'Glucose', type: FormFieldInputType.TEXT_BOX },
  { name: FormField.INITIAL_WBCS, label: 'WBCs', type: FormFieldInputType.RADIO_CHOICE, displayHorizontal: true },
  { name: FormField.INITIAL_RBCS, label: 'RBCs', type: FormFieldInputType.RADIO_CHOICE, displayHorizontal: true },
  { name: FormField.PRENATAL_CARE_PROVIDER, label: 'Prenatal Care Provider', type: FormFieldInputType.TEXT_BOX },
  { name: FormField.GRAVIDA_PARA, label: 'Gravida/Para', type: FormFieldInputType.TEXT_BOX },
  { name: FormField.ESTIMATED_DELIVERY_DATE, label: 'Estimated Delivery Date', type: FormFieldInputType.DATE },
  { name: FormField.GESTATIONAL_AGE, label: 'Gestational Age', type: FormFieldInputType.TEXT_BOX },
  { name: FormField.PRENATAL_COMPLICATIONS, label: 'Prenatal Complications', type: FormFieldInputType.TEXT_AREA, maxLength: 150 },
  { name: FormField.PRENATAL_NOTES, label: 'Prenatal Notes', type: FormFieldInputType.TEXT_AREA, maxLength: 150 },
  { name: FormField.MATERNAL_BLOOD_PRESSURE, label: 'Blood Pressure', type: FormFieldInputType.TEXT_BOX },
  { name: FormField.MATERNAL_PULSE, label: 'Pulse', type: FormFieldInputType.TEXT_BOX },
  { name: FormField.MATERNAL_TEMPERATURE, label: 'Temperature', type: FormFieldInputType.TEXT_BOX },
  { name: FormField.MATERNAL_RESPIRATIONS, label: 'Respirations', type: FormFieldInputType.TEXT_BOX },
  { name: FormField.CERVICAL_DILATION, label: 'Cervical Dilation', type: FormFieldInputType.TEXT_BOX },
  { name: FormField.EFFACEMENT, label: 'Effacement', type: FormFieldInputType.TEXT_BOX },
  { name: FormField.STATION, label: 'Station', type: FormFieldInputType.TEXT_BOX },
  { name: FormField.FETAL_HEART_RATE, label: 'Fetal Heart Rate', type: FormFieldInputType.TEXT_BOX },
  { name: FormField.INITIAL_ASSESSMENT_NOTES, label: 'Assessment Notes', type: FormFieldInputType.TEXT_AREA, maxLength: 150 },
  { name: FormField.PROVIDER_NOTIFIED_TIME, label: 'Provider Notified Time', type: FormFieldInputType.TIME },
  { name: FormField.PROVIDER_ORDERS, label: 'Orders', type: FormFieldInputType.TEXT_AREA },
  { name: FormField.MEDICATIONS, label: 'Medications', type: FormFieldInputType.TEXT_AREA, maxLength: 150 },
  { name: FormField.LABS_AND_DIAGNOSTICS, label: 'Labs and Diagnostics', type: FormFieldInputType.TEXT_AREA, maxLength: 150 },
  { name: FormField.INTERVENTIONS, label: 'Interventions', type: FormFieldInputType.TEXT_AREA, maxLength: 150 },
  { name: FormField.PATIENT_RESPONSE_NOTES, label: 'Patient Response', type: FormFieldInputType.TEXT_AREA, maxLength: 150 },
  { name: FormField.ADDITIONAL_NOTES, label: 'Additional Notes', type: FormFieldInputType.TEXT_AREA, maxLength: 150 },
  { name: FormField.NOTES, label: 'Notes', type: FormFieldInputType.TEXT_AREA },
  { name: FormField.NURSE_SIGNATURE, label: 'Nurse Signature', type: FormFieldInputType.TEXT_BOX },
  { name: FormField.NURSE_SIGNATURE_TIME, label: 'Time', type: FormFieldInputType.TIME },
  { name: FormField.PROVIDER_SIGNATURE, label: 'Provider Signature', type: FormFieldInputType.TEXT_BOX },
  { name: FormField.PROVIDER_SIGNATURE_TIME, label: 'Time', type: FormFieldInputType.TIME }
].map(withTextInputMaxLength);

export const STEP_CONFIGS: OBTriageStepConfig[] = [
  {
    title: 'Patient Information & Labor Assessment',
    sections: [
      {
        title: 'Patient Information & Labor Assessment',
        rows: [
          [FormField.PATIENT_NAME, FormField.AGE, FormField.PROVIDER],
          [FormField.ARRIVAL_TIME, FormField.ALLERGIES, FormField.REASON_FOR_ARRIVAL],
          [FormField.MODE, FormField.OTHER]
        ]
      },
      {
        title: 'Labor Assessment',
        rows: [[FormField.TIME_OF_ONSET]]
      },
      {
        title: 'Contractions',
        rows: [[FormField.CONTRACTION_FREQUENCY, FormField.CONTRACTION_DURATION, FormField.CONTRACTION_QUALITY]]
      },
      {
        title: 'Membranes',
        rows: [[FormField.MEMBRANES_STATUS], [FormField.MEMBRANES_TIME, FormField.COLOR_OF_FLUID, FormField.ODOR], [FormField.AMOUNT, FormField.VISCOSITY]]
      },
      {
        title: 'Fetal Activity',
        rows: [[FormField.FETAL_ACTIVITY, FormField.TIME_OF_LAST_ACTIVITY]]
      }
    ]
  },
  {
    title: 'History of Pre-Natal Care',
    sections: [
      {
        title: 'History of Pre-Natal Care',
        rows: [[FormField.CURRENT_PRENATAL_CARE, FormField.STEP2_PROVIDER_NAME]]
      },
      {
        title: 'Last Menstrual Period (LMP)',
        rows: [
          [FormField.LMP_HOW_LONG_AGO_UNIT, FormField.CERTAINTY],
          [FormField.LMP_DAYS, FormField.LMP_WEEKS]
        ]
      },
      {
        title: 'Estimate Date of Birth (EDB)',
        rows: [
          [FormField.EDB_FROM_NOW_UNIT, FormField.CONFIRMED_BY_ULTRASOUND],
          [FormField.EDB_DAYS_FROM_NOW, FormField.EDB_WEEKS_FROM_NOW, FormField.STEP2_GESTATIONAL_AGE],
          [FormField.STEP2_GRAVIDA, FormField.STEP2_PARA, FormField.STEP2_TERM],
          [FormField.STEP2_PRETERM, FormField.STEP2_ABORTION, FormField.STEP2_LIVING],
          [FormField.GROUP_B_STREP_STATUS, FormField.ISSUES_CONCERNS, FormField.PREVIOUS_HOSPITALIZATIONS_DURING_PREGNANCY]
        ]
      },
      {
        title: 'Focused History',
        rows: [
          [FormField.FOCUSED_HISTORY_ALCOHOL_USE, FormField.FOCUSED_HISTORY_ALCOHOL_USE_DETAILS],
          [FormField.FOCUSED_HISTORY_STREET_DRUG_USE, FormField.FOCUSED_HISTORY_STREET_DRUG_USE_DETAILS],
          [FormField.FOCUSED_HISTORY_INTRAVENOUS_DRUG_USE, FormField.FOCUSED_HISTORY_INTRAVENOUS_DRUG_USE_DETAILS],
          [FormField.FOCUSED_HISTORY_HIV_POSITIVE, FormField.FOCUSED_HISTORY_HIV_POSITIVE_DETAILS],
          [FormField.FOCUSED_HISTORY_HEPATITIS_B_POSITIVE, FormField.FOCUSED_HISTORY_HEPATITIS_B_POSITIVE_DETAILS]
        ]
      },
      {
        title: 'Oral Intake',
        rows: [[FormField.ORAL_INTAKE_TIME_LAST_FOOD_OR_LIQUID, FormField.ORAL_INTAKE_DESCRIBE]]
      }
    ]
  },
  {
    title: 'Initial Assessment',
    sections: [
      {
        title: 'Initial Assessment',
        rows: [
          [FormField.MATERNAL_TEMPERATURE, FormField.MATERNAL_PULSE, FormField.MATERNAL_RESPIRATIONS],
          [FormField.FETAL_HEART_RATE, FormField.MATERNAL_BLOOD_PRESSURE, FormField.INITIAL_FETAL_HEART_RATE_PATTERN],
          [FormField.INITIAL_COMMENTS],
          [FormField.INITIAL_VAGINAL_DISCHARGE, FormField.INITIAL_COLOR, FormField.INITIAL_ODOR],
          [FormField.INITIAL_PH, FormField.INITIAL_OTHER, FormField.INITIAL_FERNING],
          [FormField.INITIAL_GROUP_B_STREP_STATUS]
        ]
      },
      {
        title: 'Urine',
        rows: [
          [FormField.INITIAL_PROTEIN, FormField.INITIAL_GLUCOSE],
          [FormField.INITIAL_WBCS, FormField.INITIAL_RBCS]
        ]
      }
    ]
  },
  {
    title: 'Provider Orders',
    sections: [
      {
        title: 'Provider Orders',
        rows: [[FormField.PROVIDER_ORDERS]]
      }
    ]
  },
  {
    title: 'Notes',
    sections: [
      {
        title: 'Notes',
        rows: [[FormField.NOTES], [FormField.NURSE_SIGNATURE, FormField.NURSE_SIGNATURE_TIME], [FormField.PROVIDER_SIGNATURE, FormField.PROVIDER_SIGNATURE_TIME]]
      }
    ]
  }
];

export const FIELD_CONFIG_MAP = new Map(FIELD_CONFIGS.map((fieldConfig) => [fieldConfig.name, fieldConfig]));

export const STEP_FIELD_NAMES = STEP_CONFIGS.map((step) => Array.from(new Set(step.sections.flatMap((section) => section.rows.flatMap((row) => row)))));

export const FOCUSED_HISTORY_DETAILS_MAP: Partial<Record<FormField, FormField>> = {
  [FormField.FOCUSED_HISTORY_ALCOHOL_USE]: FormField.FOCUSED_HISTORY_ALCOHOL_USE_DETAILS,
  [FormField.FOCUSED_HISTORY_STREET_DRUG_USE]: FormField.FOCUSED_HISTORY_STREET_DRUG_USE_DETAILS,
  [FormField.FOCUSED_HISTORY_INTRAVENOUS_DRUG_USE]: FormField.FOCUSED_HISTORY_INTRAVENOUS_DRUG_USE_DETAILS,
  [FormField.FOCUSED_HISTORY_HIV_POSITIVE]: FormField.FOCUSED_HISTORY_HIV_POSITIVE_DETAILS,
  [FormField.FOCUSED_HISTORY_HEPATITIS_B_POSITIVE]: FormField.FOCUSED_HISTORY_HEPATITIS_B_POSITIVE_DETAILS
};

export const getFocusedHistoryParentField = (fieldName: FormField): FormField | undefined =>
  Object.keys(FOCUSED_HISTORY_DETAILS_MAP).find((key) => FOCUSED_HISTORY_DETAILS_MAP[key as FormField] === fieldName) as FormField | undefined;
